/**
 * Authentication Integration Layer
 *
 * This module provides seamless integration between the authentication system
 * and the unified request coordinator. It handles 401 error processing — a
 * request refused because the bearer was rotated under it is replayed; one
 * refused with the current bearer ends the session — and ensures auth flow
 * preservation while working with the retry system.
 */

import { getTokenStorage } from '../../../di/container';
import {
  type ErrorClassification,
  type RequestContext,
  type RetryCoordinationResult,
  ErrorCategory,
  ErrorPriority,
  ErrorType,
  RequestStatus,
} from '../../../types/api';
import { dayjs } from '../../../utils/date';
import {
  type BearerLifecycleService,
  getBearerLifecycle,
} from '../../auth/bearer.lifecycle.service';

import type {
  AuthErrorInterface,
  AuthIntegrationConfig,
} from '../../../types/api/integration.types';
import type { IAuthIntegration } from '../../../types/api/integration.types';
import type { AxiosRequestConfig } from 'axios';

// =============================================================================
// DEFAULT CONFIGURATION
// =============================================================================

const DEFAULT_AUTH_CONFIG: AuthIntegrationConfig = {
  enableAutoRefresh: true,
  skipAuthEndpoints: [
    '/auth/login',
    '/auth/logout',
    '/auth/register',
    '/auth/refresh',
  ],
  authHeader: 'Authorization',
  tokenPrefix: 'Bearer ',
};

// =============================================================================
// AUTHENTICATION INTEGRATION CLASS
// =============================================================================

/**
 * Authentication Integration Layer implementation.
 *
 * This class implements the IAuthIntegration interface from the centralized types,
 * providing seamless integration between the authentication system and the
 * unified request coordinator.
 */
export class AuthIntegration implements IAuthIntegration {
  private config: AuthIntegrationConfig;
  private customBearerLifecycle?: BearerLifecycleService;

  constructor(
    config: Partial<AuthIntegrationConfig> = {},
    customBearerLifecycle?: BearerLifecycleService
  ) {
    this.config = { ...DEFAULT_AUTH_CONFIG, ...config };
    this.customBearerLifecycle = customBearerLifecycle;
  }

  /**
   * Handle authentication errors and coordinate with retry system
   */
  public async handleAuthError(
    error: AuthErrorInterface
  ): Promise<RetryCoordinationResult> {
    // Check if this is actually an auth error
    if (!this.isAuthenticationError(error)) {
      const requestId = `req_${dayjs().valueOf()}_${Math.random().toString(36).substring(2, 9)}`;
      return {
        success: false,
        error,
        context: {
          id: requestId,
          config: { url: error.config?.url || '' },
          startTime: dayjs().valueOf(),
          status: RequestStatus.FAILED,
          retryCount: 0,
          maxRetries: 3,
          metadata: {
            requestId,
            timestamp: dayjs().valueOf(),
          },
        } as RequestContext,
        metrics: {
          requestId,
          startTime: dayjs().valueOf(),
          retryCount: 0,
        },
      };
    }

    // Check if we should skip auth for this endpoint
    if (this.shouldSkipAuth(error)) {
      const requestId = `req_${dayjs().valueOf()}_${Math.random().toString(36).substring(2, 9)}`;
      return {
        success: false,
        error,
        context: {
          id: requestId,
          config: { url: error.config?.url || '' },
          startTime: dayjs().valueOf(),
          status: RequestStatus.FAILED,
          retryCount: 0,
          maxRetries: 3,
          metadata: {
            requestId,
            timestamp: dayjs().valueOf(),
          },
        } as RequestContext,
        metrics: {
          requestId,
          startTime: dayjs().valueOf(),
          retryCount: 0,
        },
      };
    }

    // If auth recovery is disabled, return the original error without attempting it
    // This ensures 401 errors pass through with their original status intact
    if (!this.config.enableAutoRefresh) {
      const requestId = `req_${dayjs().valueOf()}_${Math.random().toString(36).substring(2, 9)}`;

      // Call onAuthError callback if configured
      if (this.config.onAuthError) {
        this.config.onAuthError(error);
      }

      return {
        success: false,
        error,
        context: {
          id: requestId,
          config: { url: error.config?.url || '' },
          startTime: dayjs().valueOf(),
          status: RequestStatus.FAILED,
          retryCount: 0,
          maxRetries: 3,
          metadata: {
            requestId,
            timestamp: dayjs().valueOf(),
          },
        } as RequestContext,
        metrics: {
          requestId,
          startTime: dayjs().valueOf(),
          retryCount: 0,
        },
      };
    }

    // Handle 401 error: replay if the bearer was rotated, else end the session
    if (error.status === 401 || error.response?.status === 401) {
      return await this.handle401Error(error);
    }

    // Handle 403 error (forbidden - don't retry)
    if (error.status === 403 || error.response?.status === 403) {
      return this.handle403Error(error);
    }

    // Not an auth error we can handle
    const requestId = `req_${dayjs().valueOf()}_${Math.random().toString(36).substring(2, 9)}`;
    return {
      success: false,
      error,
      context: {
        id: requestId,
        config: { url: error.config?.url || '' },
        startTime: dayjs().valueOf(),
        status: RequestStatus.FAILED,
        retryCount: 0,
        maxRetries: 3,
        metadata: {
          requestId,
          timestamp: dayjs().valueOf(),
        },
      } as RequestContext,
      metrics: {
        requestId,
        startTime: dayjs().valueOf(),
        retryCount: 0,
      },
    };
  }

  /**
   * Determine if retry should be skipped for auth errors
   */
  public shouldSkipRetry(error: AuthErrorInterface): boolean {
    // Skip retry for 403 (forbidden) - permissions issue
    if (error.status === 403 || error.response?.status === 403) {
      return true;
    }

    // Skip retry for certain endpoints
    if (this.shouldSkipAuth(error)) {
      return true;
    }

    return false;
  }

  /**
   * Update authentication configuration
   */
  public updateConfig(config: Partial<AuthIntegrationConfig>): void {
    this.config = { ...this.config, ...config };
  }

  // =============================================================================
  // PRIVATE METHODS
  // =============================================================================

  /**
   * A request was refused with a 401.
   *
   * Refreshing is no answer to that: the API refreshes a bearer that is STILL
   * VALID, and the bearer that would sign the refresh is the one that was just
   * refused. The question worth asking is whether the refusal is already out of
   * date — whether the bearer has CHANGED since this request was signed.
   *
   * It can have. A refresh rotates the bearer (the API deletes the old one),
   * and it runs in the background while other requests are in flight, signed
   * with the bearer it is about to kill. Those come back 401 although the
   * session is healthy, and the fix is to send them again with the new bearer.
   *
   * So: wait for any refresh in flight to land, then compare. A different
   * bearer in storage → replay. The same one → it is genuinely finished, and
   * the session ends.
   */
  private async handle401Error(
    error: AuthErrorInterface
  ): Promise<RetryCoordinationResult> {
    const signedWith = this.bearerOf(error);
    const lifecycle = this.bearerLifecycle();

    await lifecycle.settled();
    const current = await this.storedBearer();

    if (signedWith && current && current !== signedWith) {
      // Signal the coordinator to replay the original request; the
      // bearer-signing interceptor applies the bearer now in storage. The
      // replay bypasses the coordinator, so a second 401 is not handled again.
      return this.replayResult(error);
    }

    // The refused bearer is still the stored one: it is finished. (With no
    // bearer on the request — session auth, or a request sent before signing
    // in — there is no token session to end; the platform's handler decides.)
    if (signedWith && current === signedWith) {
      await lifecycle.endSession();
    }

    if (this.config.onAuthError) {
      this.config.onAuthError(error);
    }

    return this.failedResult(error);
  }

  /** The bearer a refused request was signed with, read back off its config. */
  private bearerOf(error: AuthErrorInterface): string | null {
    const headers = error.config?.headers as
      | ({ get?: (name: string) => unknown } & Record<string, unknown>)
      | undefined;
    if (!headers) return null;

    // `headers` is an AxiosHeaders or a plain object, depending on which
    // interceptor last rebuilt it (see the signing interceptor in client.ts).
    const value =
      typeof headers.get === 'function'
        ? headers.get(this.config.authHeader)
        : headers[this.config.authHeader];

    if (typeof value !== 'string') return null;
    if (!value.startsWith(this.config.tokenPrefix)) return null;

    return value.slice(this.config.tokenPrefix.length) || null;
  }

  private async storedBearer(): Promise<string | null> {
    try {
      return await getTokenStorage().getAccessToken();
    } catch {
      // No token storage registered → nothing to compare against.
      return null;
    }
  }

  private replayResult(error: AuthErrorInterface): RetryCoordinationResult {
    const requestId = `req_${dayjs().valueOf()}_${Math.random().toString(36).substring(2, 9)}`;

    return {
      success: true,
      response: undefined,
      context: {
        id: requestId,
        config: { url: error.config?.url || '' },
        startTime: dayjs().valueOf(),
        status: RequestStatus.RETRYING,
        retryCount: 1,
        maxRetries: 3,
        metadata: {
          requestId,
          timestamp: dayjs().valueOf(),
          retryCount: 1,
          isRetry: true,
        },
      } as RequestContext,
      metrics: {
        requestId,
        startTime: dayjs().valueOf(),
        retryCount: 1,
      },
    };
  }

  private failedResult(error: AuthErrorInterface): RetryCoordinationResult {
    const requestId = `req_${dayjs().valueOf()}_${Math.random().toString(36).substring(2, 9)}`;

    return {
      success: false,
      error,
      context: {
        id: requestId,
        config: { url: error.config?.url || '' },
        startTime: dayjs().valueOf(),
        status: RequestStatus.FAILED,
        retryCount: 0,
        maxRetries: 3,
        metadata: {
          requestId,
          timestamp: dayjs().valueOf(),
        },
      } as RequestContext,
      metrics: {
        requestId,
        startTime: dayjs().valueOf(),
        retryCount: 0,
      },
    };
  }

  private handle403Error(error: AuthErrorInterface): RetryCoordinationResult {
    // 403 means forbidden - user doesn't have permission
    // This should not be retried
    if (this.config.onAuthError) {
      this.config.onAuthError(error);
    }

    const requestId = `req_${dayjs().valueOf()}_${Math.random().toString(36).substring(2, 9)}`;

    return {
      success: false,
      error,
      context: {
        id: requestId,
        config: { url: error.config?.url || '' },
        startTime: dayjs().valueOf(),
        status: RequestStatus.FAILED,
        retryCount: 0,
        maxRetries: 3,
        metadata: {
          requestId,
          timestamp: dayjs().valueOf(),
        },
      } as RequestContext,
      metrics: {
        requestId,
        startTime: dayjs().valueOf(),
        retryCount: 0,
      },
    };
  }

  private isAuthenticationError(error: AuthErrorInterface): boolean {
    const status = error.status || error.response?.status;
    return status === 401 || status === 403;
  }

  private shouldSkipAuth(error: AuthErrorInterface): boolean {
    const url = error.config?.url || '';
    return this.config.skipAuthEndpoints.some((endpoint) =>
      url.includes(endpoint)
    );
  }

  private bearerLifecycle(): BearerLifecycleService {
    return this.customBearerLifecycle || getBearerLifecycle();
  }
}

// =============================================================================
// FACTORY FUNCTIONS
// =============================================================================

/**
 * Create an AuthIntegration instance with default configuration
 */
export function createAuthIntegration(
  config?: Partial<AuthIntegrationConfig>
): AuthIntegration {
  return new AuthIntegration(config);
}

/**
 * Create error classification for authentication errors
 */
export function classifyAuthError(
  error: AuthErrorInterface
): ErrorClassification {
  const status = error.status || error.response?.status;

  if (status === 401) {
    return {
      type: ErrorType.AUTHENTICATION,
      category: ErrorCategory.AUTH_REQUIRED,
      shouldRetry: true, // Will retry after token refresh
      shouldRefreshAuth: true,
      shouldTransform: false,
      priority: ErrorPriority.HIGH,
    };
  }

  if (status === 403) {
    return {
      type: ErrorType.AUTHORIZATION,
      category: ErrorCategory.USER_ACTION_REQUIRED,
      shouldRetry: false, // Don't retry permission errors
      shouldRefreshAuth: false,
      shouldTransform: true,
      priority: ErrorPriority.MEDIUM,
    };
  }

  return {
    type: ErrorType.HTTP,
    category: ErrorCategory.NON_RETRYABLE,
    shouldRetry: false,
    shouldRefreshAuth: false,
    shouldTransform: true,
    priority: ErrorPriority.LOW,
  };
}

/**
 * Check if an error is an authentication error
 */
export function isAuthError(error: unknown): error is AuthErrorInterface {
  if (!error || typeof error !== 'object') return false;

  const authError = error as AuthErrorInterface;
  const status = authError.status || authError.response?.status;

  return status === 401 || status === 403;
}

// =============================================================================
// ERROR CLASS
// =============================================================================

export class AuthError extends Error implements AuthErrorInterface {
  public status: number;
  public response?: {
    status: number;
    data: {
      message?: string;
      error?: string;
      errors?: Record<string, string[]>;
    };
  };
  public config?: AxiosRequestConfig;

  constructor(
    message: string,
    status = 401,
    response?: {
      status: number;
      data: {
        message?: string;
        error?: string;
        errors?: Record<string, string[]>;
      };
    },
    config?: AxiosRequestConfig
  ) {
    super(message);
    this.name = 'AuthError';
    this.status = status;
    this.response = response;
    this.config = config;
  }
}

// =============================================================================
// EXPORTS
// =============================================================================

export default AuthIntegration;
